/**
 * Trace Studio panel — Catalog tab: browse curated asset packs and import
 * or open their assets.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Catalogs } from 'trace-viewer/cms';
import type { CatalogResponseDto } from 'trace-viewer/cms';
import {
    styles,
    apiConfig,
    doImportAsset,
    doOpenAsset,
    sceneHasContent,
    ConfirmOpenBar,
    toAssetItem,
    PackCard,
    AssetCard,
    AssetInfoBar,
    Spacers,
} from '../catalog-shared';
import type { AssetItem } from '../catalog-shared';
import { FilterSortBar, sortBy, type SortKey } from '../catalog-filters';
import { cachedSlot, useMirrorSlot, useCatalogRefresh } from './state';

export const CatalogTab: React.FC<{ token: string; active: boolean }> = ({ token, active }) => {
    const [packs, setPacks] = useState<CatalogResponseDto[]>(() => cachedSlot('catalog.packs', []));
    const [selectedPack, setSelectedPack] = useState<CatalogResponseDto | null>(() => cachedSlot('catalog.selectedPack', null));
    const [status, setStatus] = useState<'loading' | 'ready' | 'error'>(
        () => cachedSlot<'loading' | 'ready' | 'error'>('catalog.status', packs.length > 0 ? 'ready' : 'loading'),
    );
    const [errorMsg, setErrorMsg] = useState('');
    const [insertingAssetId, setInsertingAssetId] = useState<string | null>(null);
    const [busyAction, setBusyAction] = useState<'import' | 'open' | false>(false);
    const [selectedAssetId, setSelectedAssetId] = useState<string | null>(() => cachedSlot('catalog.selectedAssetId', null));
    // Search is split per navigation level so opening a pack and backing out
    // preserves the packs-list filter (and vice versa).
    const [search, setSearch] = useState<string>(() => cachedSlot('catalog.search', ''));
    const [assetSearch, setAssetSearch] = useState<string>(() => cachedSlot('catalog.assetSearch', ''));
    // Pack sort preference; 'default' = the order the server sent.
    const [packSort, setPackSort] = useState<SortKey>(() => cachedSlot<SortKey>('catalog.packSort', 'name'));
    useMirrorSlot('catalog.packs', packs);
    useMirrorSlot('catalog.selectedPack', selectedPack);
    useMirrorSlot('catalog.status', status);
    useMirrorSlot('catalog.selectedAssetId', selectedAssetId);
    useMirrorSlot('catalog.search', search);
    useMirrorSlot('catalog.assetSearch', assetSearch);
    useMirrorSlot('catalog.packSort', packSort);
    const catalogsRef = useRef<Catalogs | null>(null);
    const [confirmingOpen, setConfirmingOpen] = useState(false);
    /** True while the full contents of the open pack are being fetched. */
    const [packDetailLoading, setPackDetailLoading] = useState(false);
    useEffect(() => { setConfirmingOpen(false); }, [selectedAssetId]);

    const packsRef = useRef(packs);
    useEffect(() => { packsRef.current = packs; }, [packs]);

    const fetchPacks = useCallback(async () => {
        setErrorMsg('');
        try {
            const catalogs = new Catalogs(apiConfig(token));
            catalogsRef.current = catalogs;
            const res = await catalogs.packsList();
            setPacks(res.data ?? []);
            setStatus('ready');
        } catch (err) {
            console.error('[Catalog] Failed to fetch packs:', err);
            setErrorMsg(err instanceof Error ? err.message : 'Failed to load catalog');
            // Keep showing cached data on background-refresh failure;
            // only surface the error state when there's nothing to show.
            setStatus(packsRef.current.length > 0 ? 'ready' : 'error');
        }
    }, [token, setPacks, setStatus]);

    // Hydrate the API client ref on mount (it's not in the slot store).
    useEffect(() => {
        catalogsRef.current = new Catalogs(apiConfig(token));
    }, [token]);

    // Only fetch on first mount if we don't already have cached data.
    useEffect(() => {
        if (packs.length === 0) void fetchPacks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetchPacks]);

    // If the cached selected pack holds only the list-endpoint preview subset
    // (interrupted fetch or stale cache), complete it on mount. Subsequent
    // pack opens are handled by openPack below.
    useEffect(() => {
        const pack = selectedPack;
        if (!pack?.id) return;
        const count = pack.catalogAssets?.length ?? 0;
        if (pack.totalAssetCount != null && count >= pack.totalAssetCount) return;
        let cancelled = false;
        setPackDetailLoading(true);
        void (async () => {
            try {
                const detail = await catalogsRef.current!.packsDetail(pack.id!);
                if (!cancelled) setSelectedPack(prev => (prev?.id === pack.id ? detail.data : prev));
            } catch (err) {
                console.error('[Catalog] Failed to complete pack contents:', err);
            } finally {
                if (!cancelled) setPackDetailLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refreshCurrent = useCallback(async () => {
        if (selectedPack?.id && catalogsRef.current) {
            try {
                const detail = await catalogsRef.current.packsDetail(selectedPack.id);
                setSelectedPack(detail.data);
            } catch (err) {
                console.error('[Catalog] Refresh pack failed:', err);
            }
        } else {
            await fetchPacks();
        }
    }, [selectedPack, fetchPacks]);
    useCatalogRefresh(active, refreshCurrent);

    const openPack = useCallback(async (pack: CatalogResponseDto) => {
        setAssetSearch('');
        // packsList returns only a preview subset of each pack's assets
        // (plus totalAssetCount). Enter the pack view in a loading state and
        // render the grid only once the full contents arrive (the view
        // checks packDetailLoading); skip the fetch when the preview already
        // covers the whole pack.
        setSelectedPack(pack);
        if (!catalogsRef.current || !pack.id) return;
        const previewCount = pack.catalogAssets?.length ?? 0;
        const isComplete = previewCount > 0 && pack.totalAssetCount != null && previewCount >= pack.totalAssetCount;
        if (isComplete) return;
        setPackDetailLoading(true);
        try {
            const detail = await catalogsRef.current.packsDetail(pack.id);
            // Ignore the result if the user navigated away meanwhile.
            setSelectedPack(prev => (prev?.id === pack.id ? detail.data : prev));
        } catch (err) {
            console.error('[Catalog] Failed to load pack:', err);
        } finally {
            setPackDetailLoading(false);
        }
    }, []);

    const requestOpen = useCallback((asset: AssetItem) => {
        if (sceneHasContent()) {
            setConfirmingOpen(true);
        } else {
            setBusyAction('open');
            void doOpenAsset(asset, setInsertingAssetId).finally(() => setBusyAction(false));
        }
    }, []);

    const packQuery = search.toLowerCase().trim();
    const assetQuery = assetSearch.toLowerCase().trim();

    const filteredAssets = useMemo(() => {
        const assets = (selectedPack?.catalogAssets ?? []).map(toAssetItem);
        if (!assetQuery) return assets;
        return assets.filter(a =>
            (a.title?.toLowerCase().includes(assetQuery) ?? false) ||
            (a.originalFilename?.toLowerCase().includes(assetQuery) ?? false)
        );
    }, [selectedPack, assetQuery]);

    const filteredPacks = useMemo(() => {
        const searched = packQuery ? packs.filter(p => p.name?.toLowerCase().includes(packQuery)) : packs;
        // Packs only carry createdDate — no modified date.
        return sortBy(searched, packSort, {
            name: p => p.name,
            created: p => p.createdDate,
            modified: () => undefined,
        });
    }, [packs, packQuery, packSort]);

    const crossPackAssets = useMemo(() => {
        if (!packQuery || selectedPack) return [];
        const results: { asset: AssetItem; packName: string }[] = [];
        for (const pack of packs) {
            for (const raw of pack.catalogAssets ?? []) {
                if (
                    raw.title?.toLowerCase().includes(packQuery) ||
                    raw.originalFilename?.toLowerCase().includes(packQuery)
                ) {
                    results.push({ asset: toAssetItem(raw), packName: pack.name ?? '' });
                }
            }
        }
        return results;
    }, [packs, packQuery, selectedPack]);

    const selectedAsset = useMemo(() => {
        if (!selectedAssetId) return null;
        const allAssets = selectedPack?.catalogAssets ?? [];
        const found = allAssets.find(a => a.assetId === selectedAssetId);
        if (found) return toAssetItem(found);
        // Also check cross-pack results
        for (const pack of packs) {
            for (const a of pack.catalogAssets ?? []) {
                if (a.assetId === selectedAssetId) return toAssetItem(a);
            }
        }
        return null;
    }, [selectedAssetId, selectedPack, packs]);

    if (status === 'loading') {
        return <div style={styles.center}>Loading catalog...</div>;
    }

    if (status === 'error') {
        return (
            <div style={styles.center}>
                <div>
                    {errorMsg || 'Failed to load catalog'}
                    <br />
                    <button style={styles.errorRetry} onClick={fetchPacks}>Retry</button>
                </div>
            </div>
        );
    }

    if (selectedPack) {
        return (
            <>
                <div style={styles.header}>
                    <button style={styles.backButton} onClick={() => { setSelectedPack(null); setSelectedAssetId(null); }} title="Back to packs">&#8592;</button>
                    <div style={styles.title}>{selectedPack.name ?? 'Pack'}</div>
                </div>
                <input type="text" placeholder="Search assets..." value={assetSearch} onChange={e => setAssetSearch(e.target.value)} style={styles.searchInput} />
                {selectedAsset && (
                    <>
                        <AssetInfoBar
                            asset={selectedAsset}
                            onImport={() => { setBusyAction('import'); void doImportAsset(selectedAsset, setInsertingAssetId).finally(() => setBusyAction(false)); }}
                            onOpen={() => requestOpen(selectedAsset)}
                            onDeselect={() => { setSelectedAssetId(null); setConfirmingOpen(false); }}
                            busy={insertingAssetId === selectedAsset.assetId ? busyAction : false}
                        />
                        {confirmingOpen && (
                            <ConfirmOpenBar
                                name={selectedAsset.title ?? selectedAsset.originalFilename ?? 'asset'}
                                onConfirm={() => { setConfirmingOpen(false); setBusyAction('open'); void doOpenAsset(selectedAsset, setInsertingAssetId).finally(() => setBusyAction(false)); }}
                                onCancel={() => setConfirmingOpen(false)}
                            />
                        )}
                    </>
                )}
                {packDetailLoading ? (
                    // Show a loading state until the full pack contents are in —
                    // rendering the preview subset first and then back-filling
                    // reads as a jarring layout jump.
                    <div style={styles.center}>Loading assets...</div>
                ) : filteredAssets.length === 0 ? (
                    <div style={styles.center}>{assetQuery ? 'No matching assets' : 'No assets in this pack'}</div>
                ) : (
                    <div style={styles.flexGrid}>
                        {filteredAssets.map(asset => (
                            <AssetCard
                                key={asset.assetId}
                                asset={asset}
                                onSelect={() => setSelectedAssetId(asset.assetId ?? null)}
                                onImport={() => doImportAsset(asset, setInsertingAssetId)}
                                selected={asset.assetId === selectedAssetId}
                                inserting={insertingAssetId === asset.assetId}
                            />
                        ))}
                        <Spacers />
                    </div>
                )}
            </>
        );
    }

    return (
        <>
            <input type="text" placeholder="Search packs & assets..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
            <FilterSortBar sort={packSort} onSortChange={setPackSort} sortKeys={['default', 'name', 'created']} />
            {selectedAsset && !selectedPack && (
                <>
                    <AssetInfoBar
                        asset={selectedAsset}
                        onImport={() => { setBusyAction('import'); void doImportAsset(selectedAsset, setInsertingAssetId).finally(() => setBusyAction(false)); }}
                        onOpen={() => requestOpen(selectedAsset)}
                        onDeselect={() => { setSelectedAssetId(null); setConfirmingOpen(false); }}
                        busy={insertingAssetId === selectedAsset.assetId ? busyAction : false}
                    />
                    {confirmingOpen && (
                        <ConfirmOpenBar
                            name={selectedAsset.title ?? selectedAsset.originalFilename ?? 'asset'}
                            onConfirm={() => { setConfirmingOpen(false); setBusyAction('open'); void doOpenAsset(selectedAsset, setInsertingAssetId).finally(() => setBusyAction(false)); }}
                            onCancel={() => setConfirmingOpen(false)}
                        />
                    )}
                </>
            )}
            {filteredPacks.length === 0 && crossPackAssets.length === 0 ? (
                <div style={styles.center}>{packQuery ? 'No results' : 'No packs available'}</div>
            ) : (
                <div style={styles.flexGrid}>
                    {filteredPacks.map(pack => (
                        <PackCard key={pack.id} pack={pack} onClick={() => openPack(pack)} />
                    ))}
                    {crossPackAssets.length > 0 && filteredPacks.length > 0 && (
                        <div style={{ width: '100%', fontSize: '11px', color: 'var(--colorNeutralForeground4)', padding: '4px 0 2px' }}>Assets</div>
                    )}
                    {crossPackAssets.map(({ asset }) => (
                        <AssetCard
                            key={asset.assetId}
                            asset={asset}
                            onSelect={() => setSelectedAssetId(asset.assetId ?? null)}
                            onImport={() => doImportAsset(asset, setInsertingAssetId)}
                            selected={asset.assetId === selectedAssetId}
                            inserting={insertingAssetId === asset.assetId}
                        />
                    ))}
                    <Spacers />
                </div>
            )}
        </>
    );
};
